import {
  CookedEvent,
  DeadSpace,
  EVENT_COOKED_CLICK,
  EVENT_COOKED_CHANGE,
  EVENT_COOKED_NAVIGATE,
  PageLifecycleState,
  PageLifecycleTransition,
} from '@fullstory/client-api';
import {
  Constants,
  RawEvent,
  RawEventKind,
  RawEventOfKind,
  UnloadReason,
  isUserActionEvent,
} from '@fullstory/recording-api';
import { createCookedEvent } from './createCookedEvent';
import { isRootFrame } from './isRootFrame';

interface CookedPageData {
  closed: boolean;
  duration: number;
  newCookedEvents: CookedEvent[];
  newDeadSpace: DeadSpace[];
  newLifecycleStates: PageLifecycleTransition[];
}

// Examine a new sequence of trailing raw events for a page and produce the new
// dead space and cooked events that they imply. This duplicates some of the
// logic in julia's idle_chef.go.
// TODO(sethf): If we rewrite the cook in TypeScript, we should get rid of this
// and just use the same code in both cases.
const defaultArgs = {
  lastExistingEventTime: 0,
  lastExistingLifecycleState: PageLifecycleState.ACTIVE,
  lastExistingPageUrl: '',
  lastExistingUserActionTime: 0,
};
export function cookTrailingEvents(
  trailingEvents: RawEvent[] = [],
  args: {
    lastExistingEventTime: number;
    lastExistingLifecycleState: PageLifecycleState | undefined;
    lastExistingPageUrl: string;
    lastExistingUserActionTime: number;
  } = defaultArgs,
): CookedPageData {
  const {
    lastExistingEventTime,
    lastExistingLifecycleState,
    lastExistingPageUrl,
    lastExistingUserActionTime,
  } = args;
  const newCookedEvents: CookedEvent[] = [];
  const newLifecycleStates: PageLifecycleTransition[] = [];
  // Determine how much the duration of this page has increased due to
  // trailing events. Because event timestamps are relative to the page start
  // time, we can determine the page duration just by looking at the timestamp
  // of the last trailing event.
  const duration = trailingEvents[trailingEvents.length - 1]?.When ?? lastExistingEventTime;

  let lastLifecycleState = lastExistingLifecycleState;
  let lastPageUrl = lastExistingPageUrl;
  let closed = lastExistingLifecycleState === PageLifecycleState.TERMINATED;

  for (const e of trailingEvents) {
    // Basic events:
    switch (e.Kind) {
      case RawEventKind.UNLOAD: {
        if (e.Args?.[0] === UnloadReason.Unknown || e.Args?.[0] === UnloadReason.Unload) {
          lastLifecycleState = PageLifecycleState.TERMINATED;
          closed = true;
          newLifecycleStates.push({
            start_offset: e.When,
            state: PageLifecycleState.TERMINATED,
          });
        }
        break;
      }
      case RawEventKind.WINDOW_FOCUS:
      case RawEventKind.WINDOW_BLUR:
      case RawEventKind.VISIBILITY_STATE: {
        const nextState = lifecycleStateTransition(e, lastLifecycleState);

        if (nextState && nextState.state !== undefined) {
          lastLifecycleState = nextState.state;
          newLifecycleStates.push(nextState);
        }

        break;
      }
      case RawEventKind.NAVIGATE:
        if (e.FId != undefined && e.FId > 0) {
          break;
        }
        lastPageUrl = e.Args?.[0];
        newCookedEvents.push(
          createCookedEvent(e, EVENT_COOKED_NAVIGATE, {
            PageUrl: lastPageUrl,
          }),
        );
        break;
      case RawEventKind.CLICK:
        newCookedEvents.push(
          createCookedEvent(e, EVENT_COOKED_CLICK, {
            PageUrl: lastPageUrl,
          }),
        );
        break;
      case RawEventKind.VALUECHANGE:
        newCookedEvents.push(
          createCookedEvent(e, EVENT_COOKED_CHANGE, {
            PageUrl: lastPageUrl,
            TargetSelector: `[_fsid="${e.Args?.[0]}"]`,
            TargetText: e.Args?.[1],
          }),
        );
        break;
    }
  }

  // compute dead space separately on sorted user action events for efficiency
  const newDeadSpace: DeadSpace[] = [];
  let lastUserActionTime = lastExistingUserActionTime;
  const sortedTrailingUserActionEvents = trailingEvents
    .filter(e => isUserActionEvent(e.Kind))
    .sort((a, b) => a.When - b.When);
  for (const e of sortedTrailingUserActionEvents) {
    // Calculates dead space ranges between user actions that are over the inactivity threshold
    if (e.When < lastUserActionTime) {
      // We got an event out of order so we need to re-evaluate previously computed dead spaces
      maybeRecomputeDeadSpace(newDeadSpace, e);
    } else if (e.When - lastUserActionTime > Constants.InactivityThreshold) {
      newDeadSpace.push({
        Start: lastUserActionTime,
        End: e.When,
      });
    }
    lastUserActionTime = e.When;
  }

  return { closed, duration, newDeadSpace, newCookedEvents, newLifecycleStates };
}

// NOTE: this logic must be kept in sync with web prepping logic in julia
function lifecycleStateTransition(
  e: RawEventOfKind<'WINDOW_BLUR' | 'WINDOW_FOCUS' | 'VISIBILITY_STATE'>,
  prevState: PageLifecycleState | undefined,
): PageLifecycleTransition | undefined {
  if (!isRootFrame(e.FId, e.PIds)) return undefined;
  if (prevState === PageLifecycleState.TERMINATED) return undefined;

  switch (e.Kind) {
    // The next lifecycle state can depend on the previous one.
    // This state machine is documented at the link below:
    // https://web.archive.org/web/20221013060048/https://developer.chrome.com/blog/page-lifecycle-api/#overview-of-page-lifecycle-states-and-events
    case RawEventKind.WINDOW_FOCUS: {
      return {
        start_offset: e.When,
        state: PageLifecycleState.ACTIVE,
      };
    }
    case RawEventKind.WINDOW_BLUR: {
      // A WINDOW_BLUR event should only transition a page from ACTIVE to PASSIVE, not HIDDEN to PASSIVE
      if (prevState !== PageLifecycleState.HIDDEN) {
        return {
          start_offset: e.When,
          state: PageLifecycleState.PASSIVE,
        };
      }
      return undefined;
    }
    case RawEventKind.VISIBILITY_STATE: {
      switch (e.Args?.[0]) {
        case 'hidden':
          return {
            start_offset: e.When,
            state: PageLifecycleState.HIDDEN,
          };
        case 'visible':
          // A VISIBILITY_STATE event with Args[0] === 'visible' should only transition a page from
          // HIDDEN to PASSIVE, not ACTIVE to PASSIVE.
          if (prevState !== PageLifecycleState.ACTIVE) {
            return {
              start_offset: e.When,
              state: PageLifecycleState.PASSIVE,
            };
          }
          return undefined;
        default:
          break;
      }
    }
  }

  return undefined;
}

/**
 * Method mutates the `deadSpaces` array if an item is invalid.
 *
 * Note: See corresponding method in `idle_chef.go`
 */
function maybeRecomputeDeadSpace(newDeadSpace: DeadSpace[], e: RawEvent) {
  // Find the existing dead space that contains the `preppedEvt.When` in its interval. e.g.
  // Remove it if the `preppedEvt` breaks up the dead space completely.
  // - Remove a dead space of [0, 6000] when there's user activity at 3000
  // Replace it with 1 or 2 new dead spaces with the preppedEvt as a boundary. e.g.
  // - For a dead space at [0, 10000] and user activity at 3000, remove it and add a new dead space [3000, 10000]
  // - For a dead space at [0, 10000] and user activity at 7000, remove it and add a new dead space [0, 7000]
  // - For a dead space at [0, 10000] and user activity at 5000, remove it and add 2 new dead spaces [0, 5000] and [5000, 10000]
  // Examples assume an inactivity threshold of 4000.
  for (let i = 0; i < newDeadSpace.length; i++) {
    const deadSpace = newDeadSpace[i];

    // Sanity check
    if (!deadSpace) {
      continue;
    }

    // remove deadspace at index if it intersects with the event
    if (deadSpace.Start <= e.When && e.When <= deadSpace.End) {
      const elementsAfter = newDeadSpace.slice(i + 1);
      newDeadSpace.splice(i, newDeadSpace.length - i); // remove deadspace at index
      newDeadSpace.push(...elementsAfter);
    }

    // Add dead space
    if (e.When - deadSpace.Start > Constants.InactivityThreshold) {
      newDeadSpace.push({
        Start: deadSpace.Start,
        End: e.When,
      });
    }

    if (deadSpace.End - e.When > Constants.InactivityThreshold) {
      newDeadSpace.push({
        Start: e.When,
        End: deadSpace.End,
      });
    }
  }
}
